<template>
  <div
    class="border rounded-lg overflow-hidden transition-colors"
    :class="isInternalNote ? 'border-amber-300 bg-amber-50' : 'border-gray-300 bg-white'"
  >
    <!-- Reply / Internal Note toggle -->
    <div v-if="showModeToggle" class="flex items-center border-b px-3 py-1.5" :class="isInternalNote ? 'border-amber-200 bg-amber-100' : 'border-gray-200 bg-gray-50'">
      <div class="inline-flex rounded-md overflow-hidden border" :class="isInternalNote ? 'border-amber-300' : 'border-gray-300'">
        <button
          class="text-[11px] font-medium px-3 py-1 transition-colors"
          :class="!isInternalNote ? 'bg-white text-gray-700 shadow-sm' : 'text-amber-600 hover:bg-amber-50'"
          @click="switchMode('reply')"
        >
          <FontAwesomeIcon :icon="['fas', 'reply']" class="mr-1" />
          Reply
        </button>
        <button
          class="text-[11px] font-medium px-3 py-1 transition-colors"
          :class="isInternalNote ? 'bg-amber-200 text-amber-800 shadow-sm' : 'text-gray-500 hover:bg-gray-100'"
          @click="switchMode('internal_note')"
        >
          <FontAwesomeIcon :icon="['fas', 'lock']" class="mr-1" />
          Internal Note
        </button>
      </div>
      <span v-if="isInternalNote" class="text-[11px] text-amber-700 ml-2">
        <FontAwesomeIcon :icon="['fas', 'lock']" class="mr-1" />
        Internal note — not visible to the customer.
      </span>
    </div>

    <!-- Text area -->
    <div class="relative">
      <textarea
        ref="textarea"
        v-model="text"
        class="w-full px-3 py-2 text-sm resize-none overflow-y-auto max-h-40 border-none focus:outline-none"
        :class="isInternalNote ? 'bg-amber-50 placeholder-amber-400' : 'bg-white'"
        rows="1"
        :placeholder="isInternalNote ? 'Add an internal note (only visible to staff)...' : showModeToggle ? 'Reply to the customer...' : 'Reply to Kairon Support...'"
        @keydown="onKeydown"
        @input="onInput"
        @blur="onBlur"
        @paste="onPaste"
      ></textarea>

      <!-- @-mention picker (staff composer only) -->
      <div
        v-if="mentionOpen && mentionResults.length"
        class="absolute left-2 right-2 bottom-full mb-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg max-h-44 overflow-y-auto"
      >
        <button
          v-for="(u, i) in mentionResults"
          :key="u.id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm"
          :class="i === mentionIndex ? 'bg-primary-50' : 'hover:bg-gray-50'"
          @mousedown.prevent="selectMention(u)"
        >
          <span class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-600 shrink-0 sensitive" data-demo-blur>
            {{ initials(u.full_name) }}
          </span>
          <span class="text-gray-800 truncate sensitive">{{ u.full_name }}</span>
          <span class="text-[11px] text-gray-400 ml-auto truncate sensitive">{{ u.email }}</span>
        </button>
      </div>
    </div>

    <!-- Character count warning -->
    <div v-if="text.length > 45000" class="px-3 pb-1">
      <span class="text-xs" :class="text.length > 50000 ? 'text-red-500' : 'text-amber-500'">
        {{ text.length.toLocaleString() }} / 50,000 characters
      </span>
    </div>

    <!-- Attachment upload. Files stage here and only upload when the comment
         is sent, so a pasted screenshot never lands on the ticket by itself. -->
    <div v-if="requirementId" class="px-3 pb-2">
      <RequirementAttachmentUpload
        ref="attachUpload"
        :requirement-id="null"
        drop-label="to your reply"
        deferred-verb="send"
        @files-changed="stagedCount = $event.length"
        @busy="attachmentsUploading = $event"
      />
    </div>

    <!-- Mention chips: who will be notified -->
    <div v-if="mentionsEnabled && activeMentions.length" class="flex flex-wrap items-center gap-1 px-3 pb-2">
      <span
        v-for="m in activeMentions"
        :key="m.id"
        class="inline-flex items-center gap-1 text-[11px] bg-primary-50 text-primary-700 rounded px-1.5 py-0.5"
      >
        @{{ m.name }}
        <button
          type="button"
          class="hover:text-primary-900"
          :aria-label="`Remove ${m.name}`"
          @click="removeMention(m)"
        >
          <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-[9px]" />
        </button>
      </span>
      <span class="text-[10px] text-gray-400">will be notified</span>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center justify-between px-3 py-1.5 border-t"
      :class="isInternalNote ? 'border-amber-200 bg-amber-100' : 'border-gray-200 bg-gray-50'"
    >
      <div class="flex items-center gap-2">
        <span v-if="submitting" class="text-[10px] text-gray-400">
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
          Sending...
        </span>
        <!-- Say why Send is disabled. A greyed-out button with no reason is
             the same dead end as a bare "Failed". -->
        <span v-else-if="attachmentsUploading" class="text-[10px] text-gray-400">
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
          Attaching file...
        </span>
        <span class="text-[10px] text-gray-400">
          {{ isMac ? '⌘' : 'Ctrl' }}+Enter to send
        </span>
        <span v-if="mentionsEnabled" class="text-[10px] text-gray-400">
          · <span class="font-semibold">@</span> to notify a teammate
        </span>
      </div>
      <button
        class="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded transition-colors disabled:opacity-50"
        :class="isInternalNote
          ? 'bg-amber-600 text-white hover:bg-amber-700'
          : 'bg-primary-500 text-white hover:bg-primary-600'"
        :disabled="!canSubmit"
        @click="submit"
      >
        <FontAwesomeIcon :icon="['fas', 'paper-plane']" class="text-[9px]" />
        {{ isInternalNote ? 'Add Note' : 'Send' }}
      </button>
    </div>

    <!-- Error message -->
    <div v-if="errorMessage" class="px-3 pb-2">
      <p class="text-xs text-red-500">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import RequirementAttachmentUpload from './RequirementAttachmentUpload.vue';

export default {
  name: 'RequirementCommentComposer',
  components: { FontAwesomeIcon, RequirementAttachmentUpload },
  props: {
    requirementId: { type: [Number, String], required: true },
    showModeToggle: { type: Boolean, default: false },
    // 'reply' or 'internal_note'. The thread passes 'reply' for a
    // customer-facing ticket and 'internal_note' for an internal one.
    defaultMode: { type: String, default: 'reply' },
  },
  emits: ['comment-posted'],
  data() {
    return {
      text: '',
      mode: this.showModeToggle ? this.defaultMode : 'reply',
      submitting: false,
      errorMessage: '',
      attachmentIds: [],
      stagedCount: 0,
      attachmentsUploading: false,
      // @-mention state (staff composer only).
      mentionUsers: [],
      mentionOpen: false,
      mentionQuery: '',
      mentionStart: -1,
      mentionIndex: 0,
      mentions: [],
    };
  },
  computed: {
    isInternalNote() {
      return this.mode === 'internal_note';
    },
    mentionsEnabled() {
      // Only the staff composer can @-mention Kairon staff.
      return this.showModeToggle;
    },
    activeMentions() {
      // Only mentions whose @token is still present in the body will notify.
      return this.mentions.filter((m) => this.text.includes(`@${m.name}`));
    },
    mentionResults() {
      if (!this.mentionOpen) return [];
      const q = this.mentionQuery.toLowerCase();
      return this.mentionUsers
        .filter(
          (u) =>
            !q ||
            u.full_name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q)
        )
        .slice(0, 6);
    },
    canSubmit() {
      if (this.submitting) return false;
      // Sending mid-upload posts the comment without the file, silently —
      // attachmentIds only fills once the upload resolves.
      if (this.attachmentsUploading) return false;
      if (this.text.length > 50000) return false;
      return this.text.trim().length > 0 || this.stagedCount > 0;
    },
    isMac() {
      return navigator.platform?.includes('Mac') || navigator.userAgent?.includes('Mac');
    },
  },
  mounted() {
    if (this.mentionsEnabled) this.loadMentionUsers();
  },
  methods: {
    // Scroll into view and take the cursor. Used by the drawer's "Waiting on
    // you" call to action.
    focus() {
      const el = this.$refs.textarea;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    },
    switchMode(newMode) {
      this.mode = newMode;
    },
    async loadMentionUsers() {
      try {
        const { data } = await axios.get('/api/user/', {
          params: { include_staff: 'true' },
        });
        this.mentionUsers = (data || []).filter((u) =>
          u.email?.endsWith('@kaironhealth.com')
        );
      } catch {
        this.mentionUsers = [];
      }
    },
    onInput() {
      this.autoResize();
      if (!this.mentionsEnabled) return;
      const el = this.$refs.textarea;
      const caret = el ? el.selectionStart : this.text.length;
      const upto = this.text.slice(0, caret);
      // Match an @-token at the caret, preceded by start-of-line or whitespace.
      const m = upto.match(/(?:^|\s)@(\w*)$/);
      if (m) {
        this.mentionOpen = true;
        this.mentionQuery = m[1];
        this.mentionStart = caret - m[1].length - 1;
        this.mentionIndex = 0;
      } else {
        this.closeMention();
      }
    },
    onBlur() {
      // Delay so an @mousedown selection registers before the picker closes.
      setTimeout(() => this.closeMention(), 100);
    },
    onPaste(event) {
      // Screenshots arrive on the clipboard, not as files. The uploader stages
      // any image on the clipboard and leaves a plain text paste alone.
      this.$refs.attachUpload?.handlePaste(event);
    },
    closeMention() {
      this.mentionOpen = false;
      this.mentionQuery = '';
      this.mentionStart = -1;
    },
    selectMention(u) {
      const before = this.text.slice(0, this.mentionStart);
      const after = this.text.slice(this.mentionStart + 1 + this.mentionQuery.length);
      const token = `@${u.full_name}`;
      this.text = `${before}${token} ${after}`;
      if (!this.mentions.some((m) => m.id === u.id)) {
        this.mentions.push({ id: u.id, name: u.full_name });
      }
      this.closeMention();
      this.$nextTick(() => {
        const pos = (before + token + ' ').length;
        const el = this.$refs.textarea;
        if (el) {
          el.focus();
          el.setSelectionRange(pos, pos);
        }
      });
    },
    removeMention(m) {
      // Drop the chip and strip its @token from the body.
      this.mentions = this.mentions.filter((x) => x.id !== m.id);
      this.text = this.text.replace(`@${m.name}`, '').replace(/\s{2,}/g, ' ');
    },
    initials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    autoResize() {
      const el = this.$refs.textarea;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    resetTextareaHeight() {
      const el = this.$refs.textarea;
      if (!el) return;
      el.style.height = '';
    },
    onKeydown(event) {
      // When the mention picker is open, arrow/enter/tab/escape drive it.
      if (this.mentionOpen && this.mentionResults.length) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.mentionIndex = Math.min(this.mentionIndex + 1, this.mentionResults.length - 1);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.mentionIndex = Math.max(this.mentionIndex - 1, 0);
          return;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          this.selectMention(this.mentionResults[this.mentionIndex]);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closeMention();
          return;
        }
      }
      if (event.key === 'Escape' && this.text.trim()) {
        // A half-typed reply is not something Escape should throw away.
        event.stopPropagation();
        return;
      }
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        if (this.canSubmit) this.submit();
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.errorMessage = '';
      try {
        const uploader = this.$refs.attachUpload;
        if (uploader?.hasFiles()) {
          const staged = uploader.getStagedFiles().length;
          this.attachmentIds = await uploader.uploadAllStaged(this.requirementId);
          if (this.attachmentIds.length < staged) {
            this.errorMessage = 'Some attachments failed to upload. Remove or retry them before sending.';
            return;
          }
        }
        const payload = {
          requirement: this.requirementId,
          body: this.text.trim(),
          source: this.isInternalNote ? 'internal_note' : 'web',
        };
        if (this.attachmentIds.length) {
          payload.attachment_ids = this.attachmentIds;
        }
        if (this.activeMentions.length) {
          payload.mentions = this.activeMentions.map((m) => m.id);
        }
        const { data } = await axios.post('/api/requirement-comments/', payload);
        this.text = '';
        this.attachmentIds = [];
        this.stagedCount = 0;
        this.mentions = [];
        this.$refs.attachUpload?.clear();
        this.resetTextareaHeight();
        this.$emit('comment-posted', data);
      } catch (err) {
        const detail = err.response?.data?.detail || err.response?.data?.message;
        this.errorMessage = detail || 'Failed to post comment. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
